import React, { FC, useEffect, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns } from './constants';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, ExportTypeType, TableRefType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import {
    getApplicationStatusMessagesListFetch,
    updateApplicationStatusMessageFetch
} from 'src/resources/applicationStatusMessagesList/reducer';
import LaunchIcon from '@mui/icons-material/Launch';
import { langCodes } from 'src/resources/constantsCodes/constants';
import { ApplicationStatusMessageType } from 'src/types/applicationStatusMessage';
import Tooltip from 'src/components/Tooltip';
import SimpleSwitch from 'src/components/SimpleSwitch';
import { applicationMessageColorOptionFce } from '../../utils/colorOptionFce';

const ApplicationStatusMessagesListContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>();

    const loading = useSelector((state: RootStoreType) => state.applicationStatusMessagesList.loading);
    const applicationStatusMessageData = useSelector(
        (state: RootStoreType) => state.applicationStatusMessagesList.data
    );
    const applicationMessageCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.applicationMessageCodes
    );
    const applicationMessageCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.applicationMessageCodes
    );
    const applicationMessageNotificationTypesCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.applicationMessageNotificationTypesCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);

    const permissionCreate = useCheckPermission('APPLICATION_STATUS_MESSAGE.CREATE');
    const permissionDetail = useCheckPermission('APPLICATION_STATUS_MESSAGE.DETAIL');
    const permissionUpdate = useCheckPermission('APPLICATION_STATUS_MESSAGE.UPDATE');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('applicationStatusMessages.title') }));
        dispatch(getApplicationStatusMessagesListFetch());
    }, []);

    const reloadApplicationStatusMessagesList = () => {
        dispatch(getApplicationStatusMessagesListFetch());
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('applicationStatusMessages.action.exportFileName'));
    };

    const handleToggleActive = (message: ApplicationStatusMessageType) => {
        dispatch(
            updateApplicationStatusMessageFetch({
                idApplicationStatusMessage: message.ID,
                data: { ...message, ACTIVE: message.ACTIVE ? 0 : 1 }
            })
        );
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'ACTIVE':
                return (
                    <Tooltip
                        title={translate(
                            !row.ACTIVE
                                ? 'applicationStatusMessages.action.activate'
                                : 'applicationStatusMessages.action.deactivate'
                        )}
                    >
                        <div>
                            <SimpleSwitch
                                value={!!row.ACTIVE}
                                disabled={!permissionUpdate}
                                onChange={() => handleToggleActive(row)}
                            />
                        </div>
                    </Tooltip>
                );
            case 'TYPE':
                return (
                    <span style={{ color: applicationMessageCodesObject[row.TYPE]?.color }}>
                    {applicationMessageCodesObject[row.TYPE]?.label}
                </span>
                );
            default:
                return row[column.key];
        }
    };

    return (
        <>
            <TopBlock
                reloadApplicationStatusMessagesList={reloadApplicationStatusMessagesList}
                permissionCreate={permissionCreate}
                handleExport={handleExport}
            />
            <TableSimple
                tableRef={tableRef}
                rowIdKey="ID"
                rows={applicationStatusMessageData || []}
                loading={loading}
                columsLabelPrefix="applicationStatusMessages.table"
                allColumns={allColumns}
                initSort={{ column: 'ID', direction: 'DESC' }}
                initFilters={[{ column: 'ACTIVE', operator: 'in', value: ['1'] }]}
                rowActions={[
                    {
                        icon: <LaunchIcon />,
                        label: translate('applicationStatusMessages.action.applicationStatusMessageDetail'),
                        getLink: (row) => `/admin/application_status_messages/${row.ID}/`,
                        hiddenFce: () => !permissionDetail
                    }
                ]}
                selectOptions={{
                    langCodes,
                    applicationMessageCodes,
                    boolNumberCodes,
                    applicationMessageNotificationTypesCodes
                }}
                heightVariant="top-tab"
                fullHeight
                optionsSessionName="applicationStatusMessageTable"
                customRender={customRender}
                optionStyleFce={applicationMessageColorOptionFce}
            />
        </>
    );
};

export default ApplicationStatusMessagesListContainer;
